import { useEffect } from 'react';

import useAsyncCallback from '@zenbusiness/application-commons-hooks/useAsyncCallback';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import useBreakpoints from '@zenbusiness/zui/useBreakpoints';

import ExtraInfoCard from '#client/components/ExtraInfoCard';
import { formatOptionsForCart } from '#client/components/MultipleJurisdictionsSelection';
import PageHeader from '#client/components/PageHeader';
import ProductOfferView from '#client/components/ProductOfferView';
import useFlowState from '#client/hooks/flow/useFlowState';
import useAddToCart from '#client/hooks/useAddToCart';
import useFlowNavigation from '#client/hooks/useFlowNavigation';
import useProductOfferViewProps from '#client/hooks/useProductOfferViewProps';
import useRemoveFromCart from '#client/hooks/useRemoveFromCart';
import TwoColumnLayout from '#client/layouts/TwoColumnLayout';

const MESSAGE_KEY = 'shopping.page.advancedCompliance';

export const ADVANCED_COMPLIANCE = 'worry_free_service_advanced_addon';
export const BASIC_COMPLIANCE = 'worry_free_service_basic';

export const AC_PRICE_ID = 'advancedCompliance';

export default function AdvancedCompliancePage() {
  const { goToNextPage } = useFlowNavigation();
  const {
    businessInfo: { cora, previousJurisdictionState }
  } = useFlowState();
  // Adding basic compliance to exclusive services to prevent the user
  // from having both basic and advanced compliance in the cart at the same time
  const { addedToCart, addToCart } = useAddToCart({
    exclusiveServices: [BASIC_COMPLIANCE],
    protectedServices: [ADVANCED_COMPLIANCE]
  });
  const { removedFromCart, removeFromCart } = useRemoveFromCart();
  const { formatMessage } = useIntl();
  const { isMobile } = useBreakpoints().sizes;
  const { loading: priceListLoading, get: getProduct, priceListUuid } = usePriceList();
  const product = getProduct(ADVANCED_COMPLIANCE);
  // Getting the cora multiple jurisdictions to add the services to the cart
  const { jurisdictions: selectedStates = [] } = cora || {};

  if (!priceListLoading && !product) {
    throw new Error(`Could not find ${ADVANCED_COMPLIANCE} in pricing list ${priceListUuid}`);
  }

  const { price, servicePricingUuid } = product.pricing;

  useEffect(() => {
    if (addedToCart || removedFromCart) {
      goToNextPage();
    }
  }, [addedToCart, goToNextPage, removedFromCart]);

  const handlePrimaryActionClick = useAsyncCallback(async () => {
    // Remove the previous jurisdiction from the cart if it exists
    if (previousJurisdictionState) {
      await removeFromCart({
        servicePricingUuid: product.pricing?.servicePricingUuid,
        jurisdictionState: previousJurisdictionState
      });
    }

    // Add the service to the cart for each selected jurisdiction
    const itemsToAdd = formatOptionsForCart(
      selectedStates,
      product.pricing?.servicePricingUuid,
      product.service
    );
    await addToCart(itemsToAdd);
  }, [addToCart, servicePricingUuid, selectedStates]);

  const handleSecondaryActionClick = useAsyncCallback(async () => {
    // Build the items to remove from the cart for the current jurisdiction
    const itemsToRemove = selectedStates.map((element) => ({
      servicePricingUuid,
      jurisdictionState: element.value
    }));
    await removeFromCart(itemsToRemove);
  }, [removeFromCart, servicePricingUuid]);

  const productOfferViewProps = useProductOfferViewProps({
    id: AC_PRICE_ID,
    itemAssets: ['verifiedrounded', 'supportagentrounded', 'allinclusiverounded'],
    price,
    primaryAction: {
      'data-testid': 'advanced-compliance-primary-action',
      onClick: handlePrimaryActionClick
    },
    secondaryAction: {
      'data-testid': 'advanced-compliance-secondary-action',
      onClick: handleSecondaryActionClick
    }
  });

  return (
    <TwoColumnLayout>
      <TwoColumnLayout.SmallColumn>
        <PageHeader
          subtitle={formatMessage({ id: 'shopping.pageHeader.subtitle.advancedCompliance' })}
          title={formatMessage({ id: 'shopping.pageHeader.title.advancedCompliance' })}
        />
        <ProductOfferView {...productOfferViewProps} />
      </TwoColumnLayout.SmallColumn>
      <TwoColumnLayout.LargeColumn>
        <Stack spacing={2}>
          <ExtraInfoCard messageKey={MESSAGE_KEY} />
          <SharedAsset
            alt=""
            src={
              isMobile
                ? '/s2/raster/good-standing-doc-notification-xs.png'
                : '/s2/raster/good-standing-doc-notification-lg.png'
            }
          />
        </Stack>
      </TwoColumnLayout.LargeColumn>
    </TwoColumnLayout>
  );
}
